import { motion } from 'framer-motion'
import { formatTime, getMedalEmoji } from '../../lib/utils'
import { BoatIndicator } from '../ui/BoatIndicator'
import type { RaceParticipant } from '../../types'

interface RacerRowProps {
  participant: RaceParticipant
  index: number
  isNewRecord?: boolean
  className?: string
}

export function RacerRow({
  participant,
  index: _index,
  isNewRecord = false,
  className = '',
}: RacerRowProps) {
  const medal = getMedalEmoji(participant.current_position)

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`
        flex items-center gap-4 p-4 rounded-lg
        backdrop-glow border border-tropical-green border-opacity-30
        transition-all duration-300 hover:border-opacity-60
        ${isNewRecord ? 'shadow-neon-green' : ''}
        ${className}
      `}
    >
      {/* Position */}
      <motion.div
        animate={isNewRecord ? { scale: [1, 1.2, 1] } : {}}
        transition={{ duration: 0.6, repeat: isNewRecord ? 2 : 0 }}
        className="w-12 h-12 flex items-center justify-center rounded-full bg-tropical-green text-palm-dark font-fredoka font-700 text-lg"
      >
        {medal ? (
          <span className="text-xl">{medal}</span>
        ) : (
          participant.current_position
        )}
      </motion.div>

      {/* Boat Indicator */}
      <BoatIndicator
        boatNumber={participant.boat_number}
        color={participant.boat_color}
        size="md"
      />

      {/* Pilot Name */}
      <div className="flex-1">
        <div className="font-fredoka font-600 text-xl text-sand-light">
          {participant.pilot_name}
        </div>
        <div className="text-sm text-tropical-green">
          Laps: {participant.total_laps}
        </div>
      </div>

      {/* Best Lap */}
      <div className="text-right">
        <div className="text-xs uppercase text-gray-400 font-quicksand">
          Best Lap
        </div>
        <div className="font-fredoka font-600 text-tropical-green text-lg">
          {participant.best_lap_time
            ? formatTime(participant.best_lap_time)
            : '--:--.-'}
        </div>
      </div>

      {/* Last Lap */}
      <div className="text-right">
        <div className="text-xs uppercase text-gray-400 font-quicksand">
          Last Lap
        </div>
        <div className="font-fredoka font-600 text-paradise-blue text-lg">
          {participant.last_lap_time
            ? formatTime(participant.last_lap_time)
            : '--:--.-'}
        </div>
      </div>

      {/* Status */}
      <div className="px-3 py-1 rounded-full text-xs font-fredoka font-600 uppercase">
        {participant.status === 'racing' && (
          <span className="text-tropical-green bg-tropical-green bg-opacity-20 px-3 py-1 rounded-full">
            Racing
          </span>
        )}
        {participant.status === 'finished' && (
          <span className="text-paradise-blue bg-paradise-blue bg-opacity-20 px-3 py-1 rounded-full">
            Finished
          </span>
        )}
        {participant.status === 'dnf' && (
          <span className="text-coral-orange bg-coral-orange bg-opacity-20 px-3 py-1 rounded-full">
            DNF
          </span>
        )}
      </div>
    </motion.div>
  )
}
